import axios from 'axios';
import moment from 'moment';
import Cookies from 'universal-cookie';
import _get from 'lodash/get';
import { jwtDecode } from 'jwt-decode';
import { ErrorToast, encryptAESJSONData, handleLogoutUser } from '../views/utils/Utils';

const BASE_URL =
  process.env.REACT_APP_NODE_ENV === 'production' ? 'https://api.rocked.us' : 'https://api-staging.rocked.us';

class Service {
  constructor() {
    const service = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    service.interceptors.request.use(
      (config) =>
        new Promise((resolve) => {
          const cookies = new Cookies();
          const accessToken = cookies.get('access_token');
          const updatedConfig = { ...config };
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const utcTime = moment.utc().format('YYYY-MM-DD HH:mm:ss');
          updatedConfig.headers['x-device-time'] = utcTime;
          updatedConfig.headers['x-timezone'] = timezone;
          if (accessToken) {
            const decodedAccessToken = jwtDecode(accessToken);

            const csrfToken = encryptAESJSONData(
              {
                hash: decodedAccessToken.hash,
                timestamp: moment.utc().valueOf(),
              },
              process.env.REACT_APP_CSRF_TOKEN_KEY,
            );

            updatedConfig.headers['x-csrf-token'] = csrfToken;
            resolve(updatedConfig);
          }
        }),
    );

    service.interceptors.response.use(this.handleSuccess, this.handleError);
    this.service = service;
  }

  handleSuccess(response) {
    return _get(response, 'data.result');
  }

  handleError = (error) => {
    if (axios.isCancel(error)) throw new Error('CANCELED REQUEST');
    const errorMessages = _get(error, 'response.data.errors', []);
    const errMessage = errorMessages.length ? errorMessages.map((e) => e.msg).join(', ') : 'Something went wrong';
    if (error.response.status === 403) {
      ErrorToast("You don't have enough permissions to execute this request.");
    }
    if (error.response.status === 401) {
      ErrorToast('Something went wrong. Please login again.');
      handleLogoutUser();
      return;
    }
    throw new Error(errMessage);
  };

  setupInterceptors = (fn) => {
    // interceptor for additionl request headers
    this.service.interceptors.request.use(fn);
  };

  // update headers
  updateHeaders = (customeHeaders) => {
    Object.entries(customeHeaders).forEach(([key, val]) => {
      this.service.defaults.headers.common[key] = val;
    });
  };

  get = (path, payload, cancelToken = null) =>
    this.service.request({
      method: 'GET',
      url: path,
      responseType: 'json',
      params: payload,
      ...(cancelToken ? { cancelToken } : {}),
    });

  patch = (path, payload) =>
    this.service.request({
      method: 'PATCH',
      url: path,
      responseType: 'json',
      data: payload,
    });

  post = (path, payload, headers = {}) =>
    this.service.request({
      method: 'POST',
      url: path,
      responseType: 'json',
      data: payload,
      ...(headers ? { headers } : {}),
    });

  put = (path, payload) =>
    this.service.request({
      method: 'PUT',
      url: path,
      responseType: 'json',
      data: payload,
    });

  delete = (path, payload) =>
    this.service.request({
      method: 'DELETE',
      url: path,
      responseType: 'json',
      data: payload,
    });
}

export default new Service();
